import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  getWhatsNewItems,
  markWhatsNewAsRead,
} from "../services/whatsNewService";

const CATEGORY_FILTERS = [
  "ALL",
  "FEATURE",
  "IMPROVEMENT",
  "FIX",
  "ANNOUNCEMENT",
  "MODULE",
];

const CATEGORY_COLOR_MAP = {
  FEATURE: "green",
  IMPROVEMENT: "blue",
  FIX: "orange",
  ANNOUNCEMENT: "purple",
  MODULE: "teal",
};

const formatWhatsNewContent = (value) => {
  if (!value) return "";

  return String(value)
    .replace(/\r\n/g, "\n")
    .replace(/^#{1,6}\s*/gm, "")
    .replace(/^\s*[-*]\s+/gm, "• ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const WhatsNewPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("ALL");

  useEffect(() => {
    let mounted = true;

    const loadItems = async () => {
      setLoading(true);
      try {
        const result = await getWhatsNewItems({
          limit: 100,
          onlyUnread: false,
        });
        if (mounted) {
          setItems(result);
        }
      } catch (error) {
        if (mounted) {
          setItems([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadItems();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredItems = useMemo(() => {
    if (activeCategory === "ALL") {
      return items;
    }

    return items.filter((item) => item?.category === activeCategory);
  }, [activeCategory, items]);

  const handleOpen = async (item) => {
    const itemId = item?.id || item?._id;

    if (!item?.isRead && itemId) {
      try {
        await markWhatsNewAsRead(itemId);
        setItems((prev) =>
          prev.map((row) =>
            (row?.id || row?._id) === itemId ? { ...row, isRead: true } : row,
          ),
        );
      } catch (error) {
        // Keep navigation non-blocking even if read status update fails.
      }
    }

    if (item?.link) {
      navigate(item.link);
    }
  };

  return (
    <Box p={{ base: 4, md: 8 }}>
      <Heading size="lg" mb={2}>
        What&apos;s New
      </Heading>

      <Text color={useColorModeValue("gray.600", "gray.400")} mb={6}>
        Product updates, module releases, improvements, and fixes.
      </Text>

      <HStack spacing={2} mb={6} overflowX="auto" pb={1}>
        {CATEGORY_FILTERS.map((category) => (
          <Button
            key={category}
            size="sm"
            variant={activeCategory === category ? "solid" : "outline"}
            colorScheme={activeCategory === category ? "teal" : "gray"}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </Button>
        ))}
      </HStack>

      {loading ? (
        <Flex justify="center" py={12}>
          <Spinner color="teal.500" />
        </Flex>
      ) : filteredItems.length === 0 ? (
        <Text color={useColorModeValue("gray.500", "gray.400")}>
          No updates found.
        </Text>
      ) : (
        <VStack align="stretch" spacing={4}>
          {filteredItems.map((item) => (
            <Box
              key={item?.id || item?._id}
              p={5}
              border="1px solid"
              borderColor={useColorModeValue("gray.200", "gray.700")}
              borderRadius="xl"
              bg={useColorModeValue("white", "gray.800")}
              boxShadow="sm"
            >
              <HStack justify="space-between" align="flex-start" mb={2}>
                <Box>
                  <HStack spacing={2} mb={1} flexWrap="wrap">
                    {!item?.isRead && (
                      <Badge colorScheme="teal" borderRadius="full">
                        New
                      </Badge>
                    )}
                    <Badge
                      colorScheme={CATEGORY_COLOR_MAP[item?.category] || "blue"}
                    >
                      {item?.category || "ANNOUNCEMENT"}
                    </Badge>
                    {item?.moduleKey && (
                      <Badge variant="outline">{item.moduleKey}</Badge>
                    )}
                    {item?.version && (
                      <Badge variant="subtle">{item.version}</Badge>
                    )}
                  </HStack>

                  <Heading size="sm">{item?.title || "Product update"}</Heading>
                </Box>

                {item?.releaseDate && (
                  <Text
                    fontSize="xs"
                    color={useColorModeValue("gray.500", "gray.400")}
                  >
                    {new Date(item.releaseDate).toLocaleDateString()}
                  </Text>
                )}
              </HStack>

              <Text
                fontSize="sm"
                color={useColorModeValue("gray.700", "gray.300")}
                whiteSpace="pre-line"
              >
                {formatWhatsNewContent(
                  item?.description || item?.summary || "Product update",
                )}
              </Text>

              {item?.link && (
                <Button
                  mt={4}
                  size="sm"
                  colorScheme="teal"
                  onClick={() => handleOpen(item)}
                >
                  Open
                </Button>
              )}
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default WhatsNewPage;
